import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

interface AdminProtectedRouteProps {
    children: React.ReactElement;
    requiredModule: string;
}

const AdminProtectedRoute: React.FC<AdminProtectedRouteProps> = ({ children, requiredModule }) => {
    const { user } = useAuth();
    const navigate = useNavigate();

    // Helper function to check if user has access to a module
    const hasModuleAccess = (moduleName: string): boolean => {
        // If user has no admin_permissions array or it's empty, they have full access (original super admin)
        if (!user?.admin_permissions || user.admin_permissions.length === 0) {
            return true;
        }

        // Check if user has view permission for this module
        const permission = user.admin_permissions.find(p => p.module === moduleName);
        return permission?.can_view || false;
    };

    // Get first accessible route for the user
    const getFirstAccessibleRoute = (): string => {
        const moduleRouteMap: Record<string, string> = {
            'dashboard': '/admin',
            'support_tickets': '/admin/support-tickets',
            'shops_management': '/admin/shops',
            'payment_management': '/admin/payments',
            'settings': '/admin/settings',
        };

        // If user has no permissions array, they're original super admin - send to dashboard
        if (!user?.admin_permissions || user.admin_permissions.length === 0) {
            return '/admin';
        }

        // Find first module the user has view access to
        const accessibleModule = user.admin_permissions.find(p => p.can_view);
        if (accessibleModule && moduleRouteMap[accessibleModule.module]) {
            return moduleRouteMap[accessibleModule.module];
        }

        // Fallback to settings if they somehow have no accessible modules
        return '/admin/settings';
    };

    // Check access on mount and when user/module changes
    useEffect(() => {
        if (user && !hasModuleAccess(requiredModule)) {
            const firstRoute = getFirstAccessibleRoute();
            console.log(`🚫 Access denied to module: ${requiredModule}`);
            console.log(`↪️  Redirecting to: ${firstRoute}`);
            navigate(firstRoute, { replace: true });
        }
    }, [user, requiredModule, navigate]);

    // If user doesn't have access, don't render anything (redirect will happen in useEffect)
    if (!hasModuleAccess(requiredModule)) {
        return null;
    }

    return children;
};

export default AdminProtectedRoute;
